import React, { useMemo } from 'react';
import { SoldTicket } from '../../../types/ticket.types';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import LoadingSkeleton from '../../ui/LoadingSkeleton/LoadingSkeleton';
import styles from './ParticipantAvatars.module.css';

interface ParticipantAvatarsProps {
  participants: SoldTicket[];
  onClick?: () => void;
  loading?: boolean;
  getTranslation?: (key: string) => string;
  participantCount?: number;
}

// Color palette for avatar backgrounds (alphabetically consistent)
const AVATAR_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#F43F5E', // Rose
  '#64748B', // Slate
];

// Get color for a name alphabetically
const getColorForName = (name: string): string => {
  if (!name || name.length === 0) {
    return AVATAR_COLORS[0];
  }
  const firstLetter = name.charAt(0).toUpperCase();
  const charCode = firstLetter.charCodeAt(0);
  // Map A-Z (65-90) to color index
  const index = (charCode - 65) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const ParticipantAvatars: React.FC<ParticipantAvatarsProps> = ({
  participants,
  onClick,
  loading = false,
  getTranslation,
  participantCount
}) => {
  // Deduplicate participants by email (same user might have multiple tickets)
  const uniqueParticipants = useMemo(() => {
    const seen = new Set<string>();
    return participants.filter(participant => {
      const key = participant.email.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [participants]);

  // Get profile photo URL (check user.userAvatar first, then fallback to other fields)
  const getProfilePhoto = (participant: SoldTicket): string | null => {
    return participant.user?.userAvatar || participant.userAvatar || participant.user?.avatar || participant.avatar || participant.user?.image || participant.image || null;
  };

  // Get display name
  const getDisplayName = (participant: SoldTicket): string => {
    return `${participant.name} ${participant.lastname}`.trim();
  };

  if (loading) {
    return (
      <div className={styles.participantAvatars}>
        <div className={styles.avatarsContainer}>
          <LoadingSkeleton variant="circular" width={32} height={32} />
          <LoadingSkeleton variant="circular" width={32} height={32} />
          <LoadingSkeleton variant="circular" width={32} height={32} />
        </div>
        <LoadingSkeleton variant="text" width={120} height={14} />
      </div>
    );
  }

  if (uniqueParticipants.length === 0) {
    return null;
  }

  const visibleAvatars = uniqueParticipants.slice(0, 3);
  
  // Use participantCount if provided, otherwise use uniqueParticipants.length
  const totalCount = participantCount !== undefined ? participantCount : uniqueParticipants.length;
  const remainingCount = totalCount > 3 ? totalCount - 3 : 0;
  
  // Show "x people attending" (without +) if totalCount is 3 or less
  const showDirectCount = totalCount <= 3;

  return (
    <div 
      className={styles.participantAvatars}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className={styles.avatarsContainer}>
        {visibleAvatars.map((participant, index) => {
          const photoUrl = getProfilePhoto(participant);
          const displayName = getDisplayName(participant);
          
          return (
            <div
              key={`${participant.email}-${index}`}
              className={styles.avatarWrapper}
              style={{ zIndex: 5 - index }}
            >
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={displayName}
                  className={styles.avatar}
                  onError={(e) => {
                    // Fallback to initial icon if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      const fallback = parent.querySelector(`.${styles.avatarFallback}`);
                      if (fallback) {
                        (fallback as HTMLElement).style.display = 'flex';
                      }
                    }
                  }}
                />
              ) : null}
              <div 
                className={styles.avatarFallback}
                style={{ 
                  display: photoUrl ? 'none' : 'flex',
                  backgroundColor: getColorForName(displayName),
                  color: '#ffffff'
                }}
              >
                <ChannelInitialIcon
                  channelName={displayName}
                  size={32}
                />
              </div>
            </div>
          );
        })}
      </div>
      {showDirectCount && totalCount > 0 && (
        <span className={styles.remainingCount}>
          {totalCount} {getTranslation
            ? getTranslation('communities.peopleAttending')
            : 'kişi katılıyor'}
        </span>
      )}
      {!showDirectCount && remainingCount > 0 && (
        <span className={styles.remainingCount}>
          +{remainingCount} {getTranslation 
            ? getTranslation('communities.peopleAttending') 
            : 'kişi katılıyor'}
        </span>
      )}
      {!showDirectCount && remainingCount === 0 && totalCount > 0 && (
        <span className={styles.remainingCount}>
          {totalCount} {getTranslation
            ? getTranslation('communities.peopleAttending')
            : 'kişi katılıyor'}
        </span>
      )}
    </div>
  );
};

export default ParticipantAvatars;

